import React, { useEffect, useState } from 'react'
import { useShowContext, SimpleShowLayout, Show, useTranslate, Loading, Error } from 'react-admin'
import { Grid, Container, Chip } from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { ShowListElement } from 'src/components/uiElements/CRUD/ShowListElement'
import { CardCrudBottomButtons } from 'src/components/uiElements/CRUD/CardCrudBottomButtons'
import { getDay } from 'src/components/pages/sales/reservation/helper/getDay'
import { workUnitServices } from 'src/services/reservation/workUnit/WorkUnit.service'
import { productTypeServices } from 'src/services/reservation/product-type/product_type.service'
import { businessRuleServices } from 'src/services/sales/businessRule/businessRule.service'
import { currencyServices } from 'src/services/reservation/currency/currency.service'

const ShowWrapper = ({ children }) => <Container maxWidth='false'> {children}</Container>

const ShowContent = () => {
  const translate = useTranslate()
  const { error, isLoading, record } = useShowContext()
  const [workUnits, setWorkUnits] = useState([])
  const [productTypes, setProductTypes] = useState([])
  const [airlines, setAirlines] = useState([])
  const [destinationTypes, setDestinationTypes] = useState([])
  const [currencies, setCurrencies] = useState([])

  useEffect(() => {
    ;(async () => {
      await Promise.all([
        getWorkUnits(),
        getProductTypes(),
        getAirlines(),
        getDestinationTypes(),
        getCurrencies(),
      ])
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getWorkUnits = async () => {
    try {
      const all = await workUnitServices.getByAll()
      setWorkUnits(all)
    } catch (error) {
      setWorkUnits([])
    }
  }

  const getProductTypes = async () => {
    try {
      const all = await productTypeServices.getByParam({ limit: 999 })
      setProductTypes(all)
    } catch (error) {
      setProductTypes([])
    }
  }

  const getAirlines = async () => {
    try {
      const all = await businessRuleServices.getAirlines()
      setAirlines(all)
    } catch (error) {
      setAirlines([])
    }
  }

  const getDestinationTypes = async () => {
    try {
      const all = await businessRuleServices.getDestinationTypes()
      setDestinationTypes(all)
    } catch (error) {
      setDestinationTypes([])
    }
  }

  const getCurrencies = async () => {
    try {
      const all = await currencyServices.getByAll()
      setCurrencies(all)
    } catch (error) {
      setCurrencies([])
    }
  }

  if (isLoading) {
    return <Loading />
  }

  if (error) {
    return <Error />
  }

  const servicesMap = {
    flexi: translate('ra.field.service_flexible'),
    branded_fare: translate('ra.field.service_branded_fare'),
    ancillary_baggage: translate('ra.field.service_extra_baggage'),
    extra_services: translate('ra.field.service_hepstar'),
    service_package: translate('ra.field.service_premium_package'),
  }

  const appliedOnMap = {
    fare_amount: translate('ra.field.application_base_fare'),
    fare_amount_with_taxes: translate('ra.field.application_base_fare_taxes'),
    fare_amount_with_taxes_charges: translate('ra.field.application_base_fare_taxes_charges'),
    total_amount: translate('ra.field.application_total_amount'),
    flexi_amount: translate('ra.field.application_flexi'),
    service_package_amount: translate('ra.field.application_service_package'),
  }

  const paymentMethodMap = {
    credit_card: translate('ra.field.credit_card'),
    debit_card: translate('ra.field.debit_card'),
    debin: translate('ra.field.debin'),
    deposit: translate('ra.field.deposit'),
    transfer: translate('ra.field.transfer'),
  }

  const getPaymentMethodDisplay = (paymentMethod) => {
    if (!paymentMethod) return '-'
    // Si el valor está en formato antiguo
    if (paymentMethodMap[paymentMethod]) {
      return paymentMethodMap[paymentMethod]
    }
    // Si está en formato nuevo
    return paymentMethod
  }

  return (
    <SimpleShowLayout>
      <CardCommon
        title={translate('ra.entity.promotion')}
        actions={<CardCrudBottomButtons btnEdit={true} />}
      >
        <Grid container spacing={3}>
          <ShowListElement label='ID' value={record.id} />
          <ShowListElement label={translate('ra.field.promotion_name')} value={record.name} />
          <ShowListElement label={translate('ra.field.code')} value={record.code} />
          <ShowListElement label={translate('ra.field.site_description')} value={record.description} />
          
          <ShowListElement 
            label={translate('ra.field.apply_coupon')} 
            value={record.coupon_based ? translate('ra.message.yes') : translate('ra.message.no')} 
          />

          {record.coupon_based && record.coupons && record.coupons.length > 0 && (
            <>
              <ShowListElement 
                label={translate('ra.field.coupon_code')} 
                value={record.coupons.map(c => c.code).join(', ')} 
              />
              <ShowListElement 
                label={translate('ra.field.total_quantity')} 
                value={record.coupons.reduce((sum, c) => sum + (c.usage_limit || 0), 0)} 
              />
            </>
          )}

          <ShowListElement
            label={translate('ra.field.work_unit')}
            value={record.work_unit?.long_name || workUnits.find(w => w.id === record.work_unit_id)?.long_name}
          />

          <ShowListElement
            label={translate('ra.field.product_type')}
            value={record.product_type?.long_name || productTypes.find(p => p.id === record.product_type_id)?.long_name}
          />

          {record.product_rules && (
            <>
              <ShowListElement
                label={translate('ra.field.airline')}
                value={
                  record.product_rules.airline === null
                    ? translate('ra.field.all')
                    : record.product_rules.airline
                        ?.map(code => airlines.find(a => a.short_name === code)?.long_name || code)
                        .join(', ')
                }
              />
              <ShowListElement
                label={translate('ra.field.destination_type')}
                value={record.product_rules.destination_type || translate('ra.field.all')}
              />
            </>
          )}

          <ShowListElement
            label={translate('ra.field.required_services')}
            value={
              record.services_included && record.services_included.length > 0
                ? record.services_included.map(s => servicesMap[s] || s).join(', ')
                : '-'
            }
          />

          <ShowListElement
            label={translate('ra.field.application_value')}
            value={appliedOnMap[record.applied_on] || record.applied_on}
          />

          <ShowListElement
            label={translate('ra.field.range_currency')}
            value={record.pricing_rules?.currency_code}
          />

          {record.pricing_rules?.ranges && record.pricing_rules.ranges.length > 0 && (
            <Grid item xs={12}>
              <ShowListElement
                label={translate('ra.field.ranges')}
                value={
                  <Grid container spacing={1}>
                    {record.pricing_rules.ranges.map((range, index) => (
                      <Grid item xs={12} key={index}>
                        <Chip
                          label={`${translate('ra.field.from')}: ${range.min_value} - ${translate('ra.field.to')}: ${range.max_value || '∞'} | ${translate('ra.field.type')}: ${range.type_discount === 'fixed' ? translate('ra.field.fixed_amount') : translate('ra.field.percentage')} | ${translate('ra.field.discount')}: ${range.discount_value}${range.max_limit ? ` | ${translate('ra.field.cap')}: ${range.max_limit}` : ''}`}
                          sx={{ 
                            mb: 1, 
                            fontSize: '0.875rem',
                            height: 'auto',
                            '& .MuiChip-label': {
                              display: 'block',
                              whiteSpace: 'normal',
                              wordWrap: 'break-word',
                            }
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                }
              />
            </Grid>
          )}

          <ShowListElement
            label={translate('ra.field.validity_start')}
            value={record.starts_at ? getDay(record.starts_at.split('T')[0], false) : null}
          />
          <ShowListElement
            label={translate('ra.field.validity_end')}
            value={record.ends_at ? getDay(record.ends_at.split('T')[0], false) : null}
          />
          <ShowListElement
            label={translate('ra.field.service_start_from')}
            value={record.check_in ? getDay(record.check_in, true) : null}
          />
          <ShowListElement
            label={translate('ra.field.service_end_to')}
            value={record.check_out ? getDay(record.check_out, true) : null}
          />

          <ShowListElement
            label={translate('ra.field.site_currency')}
            value={
              record.currency?.long_name || translate('ra.field.all')
            }
          />

          <ShowListElement
            label={translate('ra.field.payment_method_optional')}
            value={getPaymentMethodDisplay(record.payment_method)}
          />

          <ShowListElement
            label={translate('ra.field.promotion_conditions')}
            value={record.terms_and_conditions_url}
          />

          <ShowListElement label={translate('ra.field.uses')} value={record.used} />

          <ShowListElement
            label={translate('ra.field.status')}
            value={
              <Chip
                label={record.is_active ? translate('ra.status.active') : translate('ra.status.inactive')}
                sx={{
                  backgroundColor: record.is_active ? '#4285F4' : '#F44336',
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            }
          />
        </Grid>
      </CardCommon>
    </SimpleShowLayout>
  )
}

export const PromotionShow = (props) => {
  return (
    <Show component={ShowWrapper} {...props} className='CrudShowContainer' actions={null}>
      <ShowContent {...props} />
    </Show>
  )
}